// src/components/Hero.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import cardDataObject from "./card-data";

/* ================================
   Animation & Layout Constants
================================ */
const GAP_PX = 4;
const PER_CARD_JITTER = 10;
const TWO_PI = Math.PI * 2;

const GRID_INSET = { top: 2, right: 2, bottom: 2, left: 2 };

/* ================================
   Data helpers
================================ */
const allCards = Object.values(cardDataObject);

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ================================
   Responsive image helpers
   - Default: Imgix-like params (?auto=format,compress&fit=crop&w=...&h=...&dpr=...&q=...)
   - For ImageKit:  return `${src}?tr=w-${w},h-${h},fo-auto,q-${q}`
   - For Cloudinary: return src.replace("/upload/", `/upload/c_fill,f_auto,q_${q},w_${w},h_${h}/`)
   - If you pre-generate sizes, just map to your files here instead.
================================ */
function buildUrl(src, { w, h, dpr = 1, q = 60 }) {
  const W = Math.round(w * dpr);
  const H = Math.round(h * dpr);
  // IMGIX / many CDNs (adjust if needed)
  return `${src}?auto=format,compress&fit=crop&w=${W}&h=${H}&dpr=${dpr}&q=${q}`;
}

export default function Hero() {
  // DOM refs
  const root = useRef(null);
  const gridRef = useRef(null);

  // Layout state - always exactly 2 rows
  const [grid, setGrid] = useState({ cols: 10, rows: 2, cardW: 100, cardH: 150 });

  // Card count: 10 on small (<650), 20 on large (>=650)
  const initialCount = typeof window !== "undefined" && window.innerWidth < 650 ? 10 : 20;
  const [numCards, setNumCards] = useState(initialCount);

  // Fixed 1px inset
  const [inset] = useState(GRID_INSET);

  // Stable deck (re-sliced when numCards changes)
  const baseDeck = useMemo(() => shuffle(allCards), []);
  const deckN = useMemo(() => baseDeck.slice(0, numCards), [baseDeck, numCards]);

  // Switch count + compute exact 2-row layout
  useEffect(() => {
    const calc = () => {
      if (!root.current) return;

      const targetCount = window.innerWidth < 650 ? 10 : 20;
      setNumCards((current) => (current === targetCount ? current : targetCount));

      const r = root.current.getBoundingClientRect();
      const isCompact = targetCount < 20;
      const safeX = isCompact ? 4 : inset.left + inset.right;
      const safeY = isCompact ? 4 : inset.top + inset.bottom;
      const availW = Math.max(0, r.width - safeX * 2);
      const availH = Math.max(0, r.height - safeY * 2);

      const rows = 2;
      const cols = Math.ceil(targetCount / rows); // 5/10 for 10/20
      const wFromWidth  = (availW - (cols - 1) * GAP_PX) / cols;
      const wFromHeight = (availH - (rows - 1) * GAP_PX) / (1.5 * rows);
      const cardW = Math.max(28, Math.min(wFromWidth, wFromHeight) * (isCompact ? 1.04 : 0.99));
      const cardH = Math.round(cardW * 1.5);

      const nextGrid = { cols, rows, cardW: Math.round(cardW), cardH };
      setGrid((current) =>
        current.cols === nextGrid.cols &&
        current.rows === nextGrid.rows &&
        current.cardW === nextGrid.cardW &&
        current.cardH === nextGrid.cardH
          ? current
          : nextGrid
      );
    };

    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(calc);
    };

    calc();
    window.addEventListener("resize", onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [inset]);

  // Animations (idle + spotlight zoom)
  useLayoutEffect(() => {
    if (!root.current || !gridRef.current) return;

    const mm = gsap.matchMedia();

    mm.add(
      {
        isMobile: "(max-width: 649px)",
        reduceMotion: "(prefers-reduced-motion: reduce)",
      },
      (context) => {
        const cards = gsap.utils.toArray(".diag-card", root.current);
        if (!cards.length) return undefined;

        const { isMobile, reduceMotion } = context.conditions;
        const motion = reduceMotion
          ? {
              floatX: isMobile ? 8 : 10,
              floatY: isMobile ? 5 : 8,
              rotate: isMobile ? 2.4 : 2.9,
              speed: isMobile ? 0.38 : 0.34,
              scale: isMobile ? 0.025 : 0.018,
            }
          : isMobile
          ? {
              floatX: 15,
              floatY: 9,
              rotate: 3.2,
              speed: 0.52,
              scale: 0.04,
            }
          : {
              floatX: 19,
              floatY: 13,
              rotate: 4.1,
              speed: 0.46,
              scale: 0.035,
            };

        gsap.fromTo(gridRef.current, { opacity: 0 }, { opacity: 1, duration: 0.55, ease: "power1.out" });

        const floaters = cards.map((el, index) => {
          const direction = index % 2 === 0 ? -1 : 1;
          const base = direction * gsap.utils.random(1.5, PER_CARD_JITTER) + gsap.utils.random(-1.25, 1.25);
          gsap.set(el, {
            x: 0,
            y: 0,
            scale: 1,
            rotation: base,
            zIndex: 1,
            transformOrigin: "50% 50%",
            force3D: true,
            willChange: "transform",
          });

          return {
            el,
            base,
            setX: gsap.quickSetter(el, "x", "px"),
            setY: gsap.quickSetter(el, "y", "px"),
            setRotation: gsap.quickSetter(el, "rotation", "deg"),
            setScale: gsap.quickSetter(el, "scale"),
            xAmp: gsap.utils.random(motion.floatX * 0.45, motion.floatX),
            yAmp: gsap.utils.random(motion.floatY * 0.5, motion.floatY),
            rotAmp: gsap.utils.random(motion.rotate * 0.35, motion.rotate),
            scaleAmp: gsap.utils.random(motion.scale * 0.45, motion.scale),
            speed: gsap.utils.random(motion.speed * 0.76, motion.speed * 1.18),
            phaseX: (index / Math.max(cards.length, 1)) * TWO_PI + gsap.utils.random(-0.35, 0.35),
            phaseY: (index / Math.max(cards.length, 1)) * TWO_PI + gsap.utils.random(0.8, 1.6),
            phaseR: (index / Math.max(cards.length, 1)) * TWO_PI + gsap.utils.random(1.2, 2.2),
            phaseS: (index / Math.max(cards.length, 1)) * TWO_PI + gsap.utils.random(2.2, 3.4),
          };
        });

        const floatCards = (time) => {
          floaters.forEach((card) => {
            const x = Math.sin(time * card.speed + card.phaseX) * card.xAmp;
            const y = Math.sin(time * card.speed * 0.82 + card.phaseY) * card.yAmp;
            const rotation = card.base + Math.sin(time * card.speed * 0.66 + card.phaseR) * card.rotAmp;
            const scale = 1 + Math.sin(time * card.speed * 0.44 + card.phaseS) * card.scaleAmp;

            card.setX(x);
            card.setY(y);
            card.setRotation(rotation);
            card.setScale(scale);
          });
        };

        gsap.ticker.add(floatCards);
        floatCards(gsap.ticker.time);

        return () => {
          gsap.ticker.remove(floatCards);
          gsap.killTweensOf(cards);
          gsap.set(cards, { zIndex: 1 });
        };
      }
    );

    return () => mm.revert();
  }, [grid]);

  const gridTop = inset.top;
  const gridLift = numCards < 20 ? 0 : -2;
  const gridAlign = "center";

  return (
    <section
      ref={root}
      className="relative z-[50] w-full overflow-x-clip mx-auto flex flex-col justify-end
                 max-w-[1150px] min-[950px]:max-w-[1200px]
                 backdrop-blur-none bg-[#06405f]/35 border border-[#19bed6]/70 rounded-md
                 shadow-[0_1px_5px_0_rgba(0,0,0,0.8),0_0_18px_rgba(25,190,214,0.16)]
                 px-4 py-2 min-h-[250px] min-[950px]:min-h-[350px]"
    >
      {/* Card Grid */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div
          ref={gridRef}
          style={{
            top: gridTop,
            right: inset.right,
            bottom: inset.bottom,
            left: inset.left,
            position: "absolute",
            display: "grid",
            gridTemplateColumns: `repeat(${grid.cols}, ${grid.cardW}px)`,
            gridAutoRows: `${grid.cardH}px`,
            gap: `${GAP_PX}px`,
            justifyContent: "center",
            alignContent: gridAlign,
            transform: `translate3d(0, ${gridLift}px, 0)`,
            opacity: 0,
            willChange: "opacity, transform",
          }}
        >
          {deckN.map((c, i) => {
            const w = grid.cardW;
            const h = grid.cardH;

            // Build right-sized URLs for 1x & 2x DPR
            const src1x = buildUrl(c.image, { w, h, dpr: 1, q: 60 });
            const src2x = buildUrl(c.image, { w, h, dpr: 2, q: 60 });

            return (
              <div key={`${c.slug || "card"}-${i}`} className="diag-card">
                <img
                  src={src1x}
                  srcSet={`${src1x} 1x, ${src2x} 2x`}
                  sizes={`${Math.ceil(w)}px`}
                  alt={c.name || "Tarot card"}
                  width={w}
                  height={h}
                  draggable={false}
                  decoding="async"
                  className="block w-full h-full object-contain rounded-[3px] shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
                />
              </div>
            );
          })}
        </div>
      </div>

    </section>
  );
}
